import { useTranslation } from "react-i18next";
import { Manager } from "../../../Manager";
import {
  fetchStores,
  storesBinaryFilters,
  type Store,
} from "../../../../state/stores";
import { useBanners, type Banner } from "../../../../state/banners";
import { supabase } from "../../../../lib/supabase";
import type { TablesInsert } from "../../../../types/supabase";
import type { SearchParams, SortParams } from "../../../Manager/types";
import { useCallback } from "react";
import { Box } from "@radix-ui/themes";

const hasCoordinates = (lat: number | null, lng: number | null) => {
  return lat !== null && lng !== null ? "Yes" : "No";
};

const SEARCH_COLUMNS = ["name", "address", "city"];

export function ManageStores() {
  const { t } = useTranslation();
  const [banners] = useBanners();

  const fetchData = useCallback(
    (
      limit?: number,
      offset?: number,
      searchParams?: SearchParams,
      sortParams?: SortParams,
    ) =>
      fetchStores(
        limit,
        offset,
        searchParams,
        SEARCH_COLUMNS,
        storesBinaryFilters,
        sortParams,
      ),
    [],
  );

  const handleSubmit = async (
    data: Record<string, unknown>,
    item: Store | null,
  ) => {
    const storeData = {
      name: data.name as string,
      reference_number: data.reference_number
        ? (data.reference_number as string)
        : null,
      address: (data.address as string) || null,
      city: (data.city as string) || null,
      state: (data.state as string) || null,
      latitude: data.latitude ? parseFloat(data.latitude as string) : null,
      longitude: data.longitude ? parseFloat(data.longitude as string) : null,
      banner_id: parseInt(data.banner_id as string, 10),
    };

    if (item) {
      // Update
      const { error } = await supabase
        .from("stores")
        .update(storeData)
        .eq("id", item.id);
      if (error) throw new Error(error.message);
    } else {
      // Create
      const { error } = await supabase
        .from("stores")
        .insert([storeData as TablesInsert<"stores">]);
      if (error) throw new Error(error.message);
    }
  };

  return (
    <Box p="4">
      <Manager.Root<Store> title={t("stores.title")} fetchData={fetchData}>
        <Manager.Search>
          <Manager.TextSearch
            columns={SEARCH_COLUMNS}
            placeholder={t("stores.searchPlaceholder")}
          />
          <Manager.BinarySearch
            filterKey="hasCoordinates"
            label={t("stores.hasCoordinates")}
            yesLabel={t("common.yes")}
            noLabel={t("common.no")}
            allLabel={t("common.all")}
          />
        </Manager.Search>

        <Manager.Sort
          columns={[
            { key: "name", isDefault: true },
            { key: "reference_number" },
            { key: "city" },
          ]}
        />

        <Manager.CreateButton label={t("stores.addStore")} />

        <Manager.Table>
          <Manager.Column<Store>
            header={t("stores.name")}
            accessor={(s) => s.name}
            sortKey="name"
          />
          <Manager.Column<Store>
            header={t("stores.referenceNumber")}
            accessor={(s) => s.reference_number ?? "—"}
            sortKey="reference_number"
          />
          <Manager.Column<Store>
            header={t("stores.address")}
            accessor={(s) => s.address ?? "—"}
          />
          <Manager.Column<Store>
            header={t("stores.city")}
            accessor={(s) => s.city ?? "—"}
            sortKey="city"
          />
          <Manager.Column<Store>
            header={t("stores.province")}
            accessor={(s) => s.state ?? "—"}
          />
          <Manager.Column<Store>
            header={t("stores.hasCoordinates")}
            accessor={(s) =>
              hasCoordinates(s.latitude, s.longitude)
                ? t("common.yes")
                : t("common.no")
            }
          />
          <Manager.Column<Store>
            header={t("stores.banner")}
            accessor={(s) => s.banners?.name ?? "—"}
          />
          <Manager.Actions>
            <Manager.EditAction />
          </Manager.Actions>
        </Manager.Table>

        <Manager.FormDialog<Store>
          title={(isEdit) =>
            isEdit ? t("stores.editStore") : t("stores.addStore")
          }
          description={(isEdit) =>
            isEdit ? t("stores.editDescription") : t("stores.createDescription")
          }
          onSubmit={handleSubmit}
          validate={(data) => (data.name as string)?.trim() !== ""}
        >
          <Manager.TextField
            name="name"
            label={t("stores.name")}
            required
            placeholder={t("stores.namePlaceholder")}
            getValue={(item) => (item as Store).name}
          />
          <Manager.TextField
            name="reference_number"
            label={t("stores.referenceNumber")}
            type="number"
            placeholder={t("stores.referenceNumberPlaceholder")}
            getValue={(item) =>
              (item as Store).reference_number?.toString() ?? ""
            }
          />
          <Manager.TextField
            name="address"
            label={t("stores.address")}
            placeholder={t("stores.addressPlaceholder")}
            getValue={(item) => (item as Store).address ?? ""}
          />
          <Manager.TextField
            name="city"
            label={t("stores.city")}
            placeholder={t("stores.cityPlaceholder")}
            getValue={(item) => (item as Store).city ?? ""}
          />
          <Manager.TextField
            name="state"
            label={t("stores.province")}
            placeholder={t("stores.provincePlaceholder")}
            getValue={(item) => (item as Store).state ?? ""}
          />
          <Manager.SelectField<Banner>
            name="banner_id"
            label={t("stores.banner")}
            placeholder={t("stores.bannerPlaceholder")}
            options={banners}
            getOptionLabel={(b) => b.name}
            getOptionValue={(b) => String(b.id)}
            getValue={(item) => String((item as Store).banner_id)}
          />
          <Manager.FieldRow>
            <Manager.Field>
              <Manager.TextField
                name="latitude"
                label={t("stores.latitude")}
                type="number"
                placeholder={t("stores.latitudePlaceholder")}
                getValue={(item) => (item as Store).latitude?.toString() ?? ""}
              />
            </Manager.Field>
            <Manager.Field>
              <Manager.TextField
                name="longitude"
                label={t("stores.longitude")}
                type="number"
                placeholder={t("stores.longitudePlaceholder")}
                getValue={(item) => (item as Store).longitude?.toString() ?? ""}
              />
            </Manager.Field>
          </Manager.FieldRow>
        </Manager.FormDialog>
      </Manager.Root>
    </Box>
  );
}
